import {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
	Component,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import { fetchAPI } from "../lib/utils";
import { toISODate } from "../lib/programVisibility";
import {
	formatStrengthRepResult,
	getStrengthPrescriptionRepsForSet,
} from "../lib/programBuilderUtils";
import { formatAmrapScoreWithRoundsLabel } from "../lib/conditioningResultUtils";
import { resolveConditioningResultPresentation } from "../lib/conditioningResultUtils";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { SearchableSelect } from "../components/SearchableSelect";
import { ExerciseDetailAccordion } from "./workouts/components/ExerciseDetailAccordion";
import AthleteDashboard, {
	formatLeaderboardMetric,
} from "../components/AthleteDashboard";
import { LeaderboardDialog } from "../components/LeaderboardDialog";
import { ExerciseHistoryDialog } from "../components/ExerciseHistoryDialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Inbox,
	AlertTriangle,
	BookOpen,
	Dumbbell,
	ChevronRight,
	ChevronLeft,
	Reply,
	Send,
	Check,
	CheckCheck,
	Loader2,
	List,
	LayoutGrid,
	User,
	Users,
	Trophy,
	History,
	ChevronDown,
	Search,
	Eye,
	MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 40;

const groupDate = (value) => {
	const iso = String(value || "").slice(0, 10);
	return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
};

const shiftGroupDate = (iso, days) => {
	const date = new Date(`${iso}T12:00:00Z`);
	date.setUTCDate(date.getUTCDate() + days);
	return date.toISOString().slice(0, 10);
};

const groupMonday = (iso) => {
	const date = new Date(`${iso}T12:00:00Z`);
	return shiftGroupDate(iso, -((date.getUTCDay() + 6) % 7));
};

const groupWeekRange = (program, week, index) => {
	const dates = (week.days || [])
		.map((day) => groupDate(day.date))
		.filter(Boolean)
		.sort();
	if (dates.length)
		return [
			groupMonday(dates[0]),
			shiftGroupDate(groupMonday(dates[dates.length - 1]), 6),
		];
	if (program.is_dated !== false) return null;
	const anchors = (program.weeks || []).flatMap((candidate, candidateIndex) => {
		const publish = groupDate(candidate.publish_date);
		const weekOffset =
			(Number(candidate.week_number) || candidateIndex + 1) - 1;
		return publish
			? [shiftGroupDate(groupMonday(publish), -weekOffset * 7)]
			: [];
	});
	const created = groupDate(program.created_at);
	const anchor = anchors.sort()[0] || (created && groupMonday(created));
	if (!anchor) return null;
	const weekOffset = (Number(week.week_number) || index + 1) - 1;
	const start = shiftGroupDate(anchor, weekOffset * 7);
	return [start, shiftGroupDate(start, 6)];
};

const groupProgramRange = (program) => {
	const ranges = (program.weeks || [])
		.map((week, index) => groupWeekRange(program, week, index))
		.filter(Boolean);
	return ranges.length ? [ranges[0][0], ranges[ranges.length - 1][1]] : null;
};

const defaultGroupWeek = (program, today) => {
	const weeks = program.weeks || [];
	const current = weeks.findIndex((week, index) => {
		const range = groupWeekRange(program, week, index);
		return range && range[0] <= today && today <= range[1];
	});
	if (current !== -1) return current;
	const past = weeks
		.map((week, index) => ({
			index,
			range: groupWeekRange(program, week, index),
		}))
		.filter(({ range }) => range && range[0] <= today)
		.sort((a, b) => b.range[0].localeCompare(a.range[0]));
	return past[0]?.index ?? 0;
};

const formatGroupRange = (range, language) => {
	if (!range) return "";
	const formatter = new Intl.DateTimeFormat(
		language === "en" ? "en-GB" : "it-IT",
		{
			day: "numeric",
			month: "short",
			year: "numeric",
			timeZone: "UTC",
		},
	);
	const format = (iso) => formatter.format(new Date(`${iso}T12:00:00Z`));
	return `${format(range[0])} – ${format(range[1])}`;
};

const formatGroupDay = (iso, language) =>
	new Intl.DateTimeFormat(language === "en" ? "en-GB" : "it-IT", {
		weekday: "short",
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	}).format(new Date(`${iso}T12:00:00Z`));

/** Guards the reused diary detail component so unusual data can't blank the page. */
class DetailErrorBoundary extends Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	render() {
		if (this.state.hasError) {
			return (
				<p className="px-3 pb-3 pt-2 text-sm text-muted-foreground">
					{this.props.fallback}
				</p>
			);
		}
		return this.props.children;
	}
}

/**
 * Prescription of one agenda row, shown when the coach expands it: the tree
 * only carries titles, so a row reading "CONDITIONING" says nothing about what
 * that session actually was. `detail` is the lazily fetched day payload, and
 * the rendering is the diary's own prescription block, so the coach reads a
 * workout here exactly as it looks everywhere else in the app.
 */
const GroupExercisePrescription = ({ detail, row, language, t }) => {
	if (!detail || detail.loading) {
		return (
			<p className="pl-5 pt-1 text-[11px] text-muted-foreground flex items-center gap-1.5">
				<Loader2 className="w-3 h-3 animate-spin" />
				{t("loading")}
			</p>
		);
	}
	if (detail.error) {
		return (
			<p className="pl-5 pt-1 text-[11px] text-destructive">{detail.error}</p>
		);
	}
	const rowId = row?.exercise_id || null;
	const rowName = String(row?.exercise_name || "")
		.trim()
		.toLowerCase();
	const exercise = (detail.exercises || []).find((item) =>
		rowId
			? item?.exercise_id === rowId
			: String(item?.exercise_name || "")
					.trim()
					.toLowerCase() === rowName,
	);
	if (!exercise) {
		return (
			<p className="pl-5 pt-1 text-[11px] text-muted-foreground">
				{t("feedbackGroupNoPrescription")}
			</p>
		);
	}
	return (
		<div className="pl-3">
			<DetailErrorBoundary fallback={t("feedbackGroupNoPrescription")}>
				{/* No `result` on the exercise: the block renders the prescription
				    only, which is all this view is about. */}
				<ExerciseDetailAccordion
					exercise={exercise}
					language={language}
					isCoachView={true}
				/>
			</DetailErrorBoundary>
		</div>
	);
};

export default function CoachFeedback() {
	const { language, t } = useLanguage();
	const { user } = useAuth();
	const { feedbackUnreadCount, refreshFeedbackUnreadCount } =
		useNotificationCounts();
	const navigate = useNavigate();
	const location = useLocation();

	const isAdmin = user?.is_admin || user?.role === "admin";

	const [view, setView] = useState("feed"); // feed | program | athlete

	// Visibility (mirrors Athletes/Programs): own by default; admin can switch
	// to "all" and optionally narrow to a specific coach.
	const [scope, setScope] = useState("mine"); // "mine" | "all"
	const [coachFilter, setCoachFilter] = useState("all");
	const [coaches, setCoaches] = useState([]);

	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextBefore, setNextBefore] = useState(null);

	// Filters
	const [onlyUnread, setOnlyUnread] = useState(false);
	const [attention, setAttention] = useState(false);
	// Show every logged result by default (incl. conditioning without note/RPE),
	// matching what the coach sees in the athlete's Results tab.
	const [includeCompleted, setIncludeCompleted] = useState(true);
	const [athleteId, setAthleteId] = useState("all");
	const [programId, setProgramId] = useState("all");
	const [period, setPeriod] = useState("30"); // "7" | "30" | "all"

	const [athletes, setAthletes] = useState([]);
	const [programs, setPrograms] = useState([]);

	// Inline reply
	const [replyingId, setReplyingId] = useState(null);
	const [replyText, setReplyText] = useState("");
	const [sending, setSending] = useState(false);
	const [feedbackThreadIds, setFeedbackThreadIds] = useState({});
	const [existingFeedbackThreadIds, setExistingFeedbackThreadIds] = useState(
		{},
	);

	// Card detail expansion + per-athlete search
	const [expandedKey, setExpandedKey] = useState(null);
	const [athleteSearch, setAthleteSearch] = useState("");
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const [isProgramPreviewOpen, setIsProgramPreviewOpen] = useState(false);
	const [programPreviewState, setProgramPreviewState] = useState(null);
	const [selectedPreviewAthlete, setSelectedPreviewAthlete] = useState(null);
	const [previewExercises, setPreviewExercises] = useState([]);
	const [activeProgramsByAthleteId, setActiveProgramsByAthleteId] = useState(
		{},
	);
	const [openingProgramPreviewId, setOpeningProgramPreviewId] = useState(null);

	// Program grid view
	const [gridProgramId, setGridProgramId] = useState("");
	const [grid, setGrid] = useState(null);
	const [gridLoading, setGridLoading] = useState(false);
	const [gridWeekIdx, setGridWeekIdx] = useState(0);

	// Group leaderboards view
	const [groups, setGroups] = useState([]);
	const [groupId, setGroupId] = useState("");
	const [groupTree, setGroupTree] = useState(null);
	const [groupTreeLoading, setGroupTreeLoading] = useState(false);
	const [groupProgramId, setGroupProgramId] = useState("");
	const [groupWeekIdx, setGroupWeekIdx] = useState(0);
	// Group exercise leaderboard dialog
	const [glbOpen, setGlbOpen] = useState(false);
	const [glbLoading, setGlbLoading] = useState(false);
	const [glbError, setGlbError] = useState("");
	const [glbData, setGlbData] = useState(null);
	const [glbExercise, setGlbExercise] = useState(null);
	const [glbGenderFilter, setGlbGenderFilter] = useState("all");
	const [glbSchemaFieldId, setGlbSchemaFieldId] = useState(null);
	// Prescriptions of the agenda rows, fetched one day at a time only when a
	// row is expanded: the tree itself stays light even with many exercises.
	const [groupDayDetails, setGroupDayDetails] = useState({});
	// Days already requested, so a second expand in the same day does not refetch.
	// A ref, not state: the check has to be synchronous inside the handler.
	const groupDayRequestedRef = useRef({});
	const [expandedGroupRows, setExpandedGroupRows] = useState({});

	// Exercise history dialog (progression over time for one feed item)
	const [historyOpen, setHistoryOpen] = useState(false);
	const [historyExercise, setHistoryExercise] = useState(null);
	const [historyResults, setHistoryResults] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState("");

	const dateFrom = (() => {
		if (period === "all") return null;
		const d = new Date();
		d.setDate(d.getDate() - (period === "7" ? 7 : 30));
		return d.toISOString().slice(0, 10);
	})();

	const buildQuery = useCallback(
		(before) => {
			const p = new URLSearchParams();
			p.set("limit", String(PAGE_SIZE));
			p.set("scope", scope);
			if (isAdmin && scope === "all" && coachFilter !== "all")
				p.set("coach_id", coachFilter);
			if (onlyUnread) p.set("only_unread", "true");
			if (attention) p.set("attention", "true");
			if (includeCompleted) p.set("include_completed", "true");
			if (athleteId !== "all") p.set("athlete_id", athleteId);
			if (programId !== "all") p.set("program_id", programId);
			if (dateFrom) p.set("date_from", dateFrom);
			if (before) p.set("before", before);
			return p.toString();
		},
		[
			onlyUnread,
			attention,
			includeCompleted,
			athleteId,
			programId,
			dateFrom,
			scope,
			coachFilter,
			isAdmin,
		],
	);

	const loadFeed = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchAPI(`/coach/feedback/feed?${buildQuery(null)}`);
			setItems(Array.isArray(data?.items) ? data.items : []);
			setNextBefore(data?.next_before || null);
		} catch (e) {
			toast.error(t("feedbackLoadError"));
			setItems([]);
			setNextBefore(null);
		} finally {
			setLoading(false);
		}
	}, [buildQuery, t]);

	const loadExistingFeedbackThreads = useCallback(async () => {
		try {
			const threads = await fetchAPI("/messages/threads");
			const mapped = {};
			(threads || []).forEach((th) => {
				const threadId = String(th?.thread_id || "").trim();
				if (!threadId.startsWith("fb:")) return;
				const resultId = threadId.split(":").pop();
				if (!resultId) return;
				mapped[resultId] = threadId;
			});
			setExistingFeedbackThreadIds(mapped);
		} catch {
			setExistingFeedbackThreadIds({});
		}
	}, []);

	const getFeedbackThreadId = useCallback(
		(item) =>
			feedbackThreadIds[item?.id] ||
			existingFeedbackThreadIds[item?.id] ||
			null,
		[feedbackThreadIds, existingFeedbackThreadIds],
	);

	const loadMore = async () => {
		if (!nextBefore || loadingMore) return;
		setLoadingMore(true);
		try {
			const data = await fetchAPI(
				`/coach/feedback/feed?${buildQuery(nextBefore)}`,
			);
			setItems((prev) => [
				...prev,
				...(Array.isArray(data?.items) ? data.items : []),
			]);
			setNextBefore(data?.next_before || null);
		} catch (e) {
			toast.error(t("feedbackLoadError"));
		} finally {
			setLoadingMore(false);
		}
	};

	// Feed/athlete views share the same feed data source.
	useEffect(() => {
		if (view === "feed" || view === "athlete") loadFeed();
	}, [view, loadFeed]);

	useEffect(() => {
		if (view !== "feed") {
			setMobileFiltersOpen(false);
		}
	}, [view]);

	useEffect(() => {
		loadExistingFeedbackThreads();
	}, [loadExistingFeedbackThreads]);

	// Coach list for admins (once).
	useEffect(() => {
		if (!isAdmin) return;
		(async () => {
			try {
				const data = await fetchAPI("/admin/coaches-with-athletes");
				setCoaches(Array.isArray(data?.coaches) ? data.coaches : []);
			} catch (e) {}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Athletes follow the visibility scope (server-side, same logic as the feed —
	// so the list always matches what the feed shows, incl. team-assigned).
	useEffect(() => {
		(async () => {
			const p = new URLSearchParams({ scope });
			if (isAdmin && scope === "all" && coachFilter !== "all")
				p.set("coach_id", coachFilter);
			try {
				const a = await fetchAPI(`/coach/feedback/athletes?${p.toString()}`);
				setAthletes(Array.isArray(a) ? a : []);
			} catch (e) {
				setAthletes([]);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scope, coachFilter]);

	// Programs follow the visibility scope (own vs all).
	useEffect(() => {
		(async () => {
			try {
				const p = await fetchAPI(
					`/programs?scope=${scope === "all" ? "all" : "mine"}&compact=true`,
				);
				setPrograms(Array.isArray(p) ? p : []);
			} catch (e) {}
		})();
	}, [scope]);

	// Program grid loader.
	const loadGrid = useCallback(
		async (pid) => {
			if (!pid) return;
			setGridLoading(true);
			try {
				const p = new URLSearchParams({ program_id: pid, scope });
				if (isAdmin && scope === "all" && coachFilter !== "all")
					p.set("coach_id", coachFilter);
				const data = await fetchAPI(
					`/coach/feedback/program-grid?${p.toString()}`,
				);
				setGrid(data || null);
				setGridWeekIdx(0);
			} catch (e) {
				toast.error(t("feedbackLoadError"));
				setGrid(null);
			} finally {
				setGridLoading(false);
			}
		},
		[t, scope, coachFilter, isAdmin],
	);

	// Reset feed filters when the visibility scope changes.
	useEffect(() => {
		setAthleteId("all");
		setProgramId("all");
	}, [scope, coachFilter]);

	// Pick a valid program for the grid within the current scope.
	useEffect(() => {
		if (view !== "program") return;
		const pool =
			isAdmin && scope === "all" && coachFilter !== "all"
				? programs.filter((p) =>
						new Set([
							...(p?.coach_ids || []),
							...(p?.coach_id ? [p.coach_id] : []),
						]).has(coachFilter),
					)
				: programs;
		const stillValid = pool.some((p) => p.program_id === gridProgramId);
		const pid = stillValid ? gridProgramId : pool[0]?.program_id || "";
		if (pid !== gridProgramId) setGridProgramId(pid);
		if (pid) loadGrid(pid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view, programs, scope, coachFilter]);

	// --- Group leaderboards ---
	// Load the coach's groups once the Gruppi pivot is opened.
	useEffect(() => {
		if (view !== "group") return;
		(async () => {
			try {
				// I gruppi seguono lo stesso scope del resto della console: il
				// server ora risponde col cono di chi chiede se non gli si chiede
				// altro, quindi la maschera "tutti" va dichiarata anche qui.
				const p = new URLSearchParams({ scope });
				if (isAdmin && scope === "all" && coachFilter !== "all")
					p.set("coach_id", coachFilter);
				const data = await fetchAPI(`/groups?${p.toString()}`);
				const list = Array.isArray(data) ? data : [];
				setGroups(list);
				setGroupId((prev) =>
					prev && list.some((g) => g.group_id === prev)
						? prev
						: list[0]?.group_id || "",
				);
			} catch (e) {
				setGroups([]);
			}
		})();
	}, [view, scope, coachFilter, isAdmin]);

	const loadGroupTree = useCallback(
		async (gid) => {
			if (!gid) {
				setGroupTree(null);
				return;
			}
			setGroupTreeLoading(true);
			try {
				const data = await fetchAPI(
					`/coach/feedback/group-leaderboards?group_id=${encodeURIComponent(gid)}`,
				);
				setGroupTree(data || null);
				const progs = [...(data?.programs || [])].sort((a, b) =>
					(groupProgramRange(b)?.[0] || "").localeCompare(
						groupProgramRange(a)?.[0] || "",
					),
				);
				const today = toISODate(new Date());
				const current = progs.find((program) => {
					const range = groupProgramRange(program);
					return range && range[0] <= today && today <= range[1];
				});
				const latestPast = progs.find(
					(program) => groupProgramRange(program)?.[0] <= today,
				);
				const fallback =
					current ||
					latestPast ||
					[...progs].reverse().find((program) => groupProgramRange(program)) ||
					progs[0];
				setGroupProgramId(
					(prev) =>
						(progs.find((program) => program.program_id === prev) || fallback)
							?.program_id || "",
				);
			} catch (e) {
				toast.error(t("feedbackLoadError"));
				setGroupTree(null);
			} finally {
				setGroupTreeLoading(false);
			}
		},
		[t],
	);

	useEffect(() => {
		if (view !== "group") return;
		loadGroupTree(groupId);
	}, [view, groupId, loadGroupTree]);

	useEffect(() => {
		const selected = groupTree?.programs?.find(
			(program) => program.program_id === groupProgramId,
		);
		if (selected)
			setGroupWeekIdx(defaultGroupWeek(selected, toISODate(new Date())));
	}, [groupProgramId, groupTree]);

	// Load a single exercise's group leaderboard (includes private results).
	const loadGroupLeaderboard = useCallback(
		async (exercise, { gender = "all", schemaFieldId = null } = {}) => {
			if (!exercise) return;
			setGlbLoading(true);
			setGlbError("");
			try {
				const p = new URLSearchParams({
					group_id: groupId,
					program_id: exercise.program_id,
					day_id: exercise.day_id,
				});
				if (exercise.exercise_id) p.set("exercise_id", exercise.exercise_id);
				if (exercise.exercise_name)
					p.set("exercise_name", exercise.exercise_name);
				if (gender && gender !== "all") p.set("gender", gender);
				if (schemaFieldId) p.set("schema_field_id", schemaFieldId);
				const data = await fetchAPI(
					`/coach/feedback/group-leaderboards/exercise?${p.toString()}`,
				);
				setGlbData(data || null);
			} catch (e) {
				setGlbError(t("feedbackLoadError"));
				setGlbData(null);
			} finally {
				setGlbLoading(false);
			}
		},
		[groupId, t],
	);

	// Lazily fetch the prescriptions of one day (cached): the agenda tree only
	// carries titles, which is not enough to recall what a "CONDITIONING" was.
	const loadGroupDayDetail = useCallback(
		async (programId, dayId) => {
			const key = `${programId}|${dayId}`;
			if (groupDayRequestedRef.current[key]) return;
			groupDayRequestedRef.current[key] = true;
			setGroupDayDetails((prev) => ({
				...prev,
				[key]: { loading: true, error: "" },
			}));
			try {
				const params = new URLSearchParams({
					group_id: groupId,
					program_id: programId,
					day_id: dayId,
				});
				const data = await fetchAPI(
					`/coach/feedback/group-leaderboards/day?${params.toString()}`,
				);
				setGroupDayDetails((prev) => ({
					...prev,
					[key]: {
						loading: false,
						error: "",
						exercises: Array.isArray(data?.exercises) ? data.exercises : [],
					},
				}));
			} catch (e) {
				// Cleared so collapsing and expanding again retries the call.
				groupDayRequestedRef.current[key] = false;
				setGroupDayDetails((prev) => ({
					...prev,
					[key]: { loading: false, error: t("feedbackLoadError") },
				}));
			}
		},
		[groupId, t],
	);

	const toggleGroupExerciseDetail = (programId, dayId, rowKey) => {
		const key = `${programId}|${dayId}|${rowKey}`;
		setExpandedGroupRows((prev) => {
			const next = { ...prev };
			if (next[key]) delete next[key];
			else next[key] = true;
			return next;
		});
		loadGroupDayDetail(programId, dayId);
	};

	const openGroupLeaderboard = (program, day, exercise) => {
		const ctx = {
			program_id: program.program_id,
			day_id: day.day_id,
			exercise_id: exercise.exercise_id || null,
			exercise_name: exercise.exercise_name || null,
			exercise_type: exercise.exercise_type || null,
			conditioning_type: exercise.conditioning_type || null,
			label: exercise.exercise_name || "-",
		};
		setGlbExercise(ctx);
		setGlbGenderFilter("all");
		setGlbSchemaFieldId(null);
		setGlbData(null);
		setGlbOpen(true);
		loadGroupLeaderboard(ctx, { gender: "all", schemaFieldId: null });
	};

	const markRead = async (toMark, read = true) => {
		const payload = toMark.map((it) => ({
			kind: it.kind,
			id: it.id,
			created_at: it.created_at,
		}));
		// Optimistic local update.
		const keys = new Set(toMark.map((it) => `${it.kind}:${it.id}`));
		setItems((prev) =>
			prev.map((it) =>
				keys.has(`${it.kind}:${it.id}`) ? { ...it, read } : it,
			),
		);
		try {
			await fetchAPI("/coach/feedback/read", {
				method: "POST",
				body: JSON.stringify({ items: payload, read }),
			});
			refreshFeedbackUnreadCount();
		} catch (e) {
			// Revert on failure.
			setItems((prev) =>
				prev.map((it) =>
					keys.has(`${it.kind}:${it.id}`) ? { ...it, read: !read } : it,
				),
			);
			toast.error(t("feedbackLoadError"));
		}
	};

	const markAllRead = async () => {
		try {
			await fetchAPI("/coach/feedback/seen", { method: "POST" });
			setItems((prev) => prev.map((it) => ({ ...it, read: true })));
			refreshFeedbackUnreadCount();
			toast.success(t("feedbackAllRead"));
		} catch (e) {
			toast.error(t("feedbackLoadError"));
		}
	};

	const openAthlete = (id) => {
		const normalizedId = String(id || "").trim();
		if (
			!normalizedId ||
			normalizedId === "undefined" ||
			normalizedId === "null"
		) {
			toast.error(t("feedbackLoadError"));
			return;
		}
		navigate(`/athletes/${normalizedId}`, {
			state: {
				fromFeedback: true,
				fromPath: `${location.pathname}${location.search}`,
			},
		});
	};

	const startReply = (item) => {
		setReplyingId(item.id);
		setReplyText("");
	};

	const openExerciseHistory = async (item) => {
		const athleteId = String(item?.athlete_id || "").trim();
		if (!athleteId) {
			toast.error(t("feedbackLoadError"));
			return;
		}
		const result = item?.result || {};
		const exercise = {
			...(item?.prescription || {}),
			exercise_id: item?.exercise_id || item?.prescription?.exercise_id || null,
			exercise_name:
				item?.exercise_name || item?.prescription?.exercise_name || "",
			exercise_type:
				result.exercise_type || item?.prescription?.exercise_type || null,
			conditioning_type:
				result.conditioning_type ||
				item?.prescription?.conditioning_type ||
				null,
		};
		setHistoryExercise(exercise);
		setHistoryResults([]);
		setHistoryError("");
		setHistoryLoading(true);
		setHistoryOpen(true);
		try {
			const p = new URLSearchParams();
			if (exercise.exercise_id) p.set("exercise_id", exercise.exercise_id);
			if (exercise.exercise_name)
				p.set("exercise_name", exercise.exercise_name);
			const data = await fetchAPI(
				`/coach/athletes/${encodeURIComponent(athleteId)}/exercise-history?${p.toString()}`,
			);
			setHistoryResults(Array.isArray(data) ? data : []);
		} catch (e) {
			setHistoryError(t("historyLoadError") || t("feedbackLoadError"));
			setHistoryResults([]);
		} finally {
			setHistoryLoading(false);
		}
	};

	const sendReply = async (item) => {
		const content = replyText.trim();
		if (!content) return;
		setSending(true);
		const threadId = `fb:${item.id}`;
		try {
			await fetchAPI("/messages", {
				method: "POST",
				body: JSON.stringify({
					receiver_id: item.athlete_id,
					thread_id: threadId,
					subject:
						language === "it"
							? `Feedback: ${item.exercise_name || item.title || "allenamento"}`
							: `Feedback: ${item.exercise_name || item.title || "workout"}`,
					content,
					message_type: "workout_feedback",
					related_program_id: item.program_id || null,
					related_workout_date: item.date || null,
					related_result_id: item.id || null,
					related_exercise_id: item.exercise_id || null,
					related_day_id: item.day_id || null,
					related_exercise_name: item.exercise_name || item.title || null,
					related_rpe: item.rpe ?? null,
					related_feedback_notes: item.notes || null,
				}),
			});
			toast.success(t("feedbackReplySent"));
			setFeedbackThreadIds((prev) => ({
				...prev,
				[item.id]: threadId,
			}));
			setExistingFeedbackThreadIds((prev) => ({
				...prev,
				[item.id]: threadId,
			}));
			setReplyingId(null);
			setReplyText("");
			if (!item.read) markRead([item], true); // replying implies reviewed
		} catch (e) {
			toast.error(t("feedbackReplyError"));
		} finally {
			setSending(false);
		}
	};

	const goToFeedbackThread = (item) => {
		const threadId = getFeedbackThreadId(item) || `fb:${item.id}`;
		navigate("/messages", { state: { openThreadId: threadId } });
	};

	const getAthleteActivePrograms = async (athleteId) => {
		if (!athleteId) return [];
		if (activeProgramsByAthleteId[athleteId]) {
			return activeProgramsByAthleteId[athleteId];
		}

		const activePrograms = await fetchAPI(
			`/workouts/coach/athletes/${athleteId}/active-programs`,
		).catch(() => []);
		const normalized = Array.isArray(activePrograms) ? activePrograms : [];
		setActiveProgramsByAthleteId((prev) => ({
			...prev,
			[athleteId]: normalized,
		}));
		return normalized;
	};

	const getPreviewExercises = async () => {
		if (previewExercises.length > 0) return previewExercises;
		const exercisesData = await fetchAPI("/exercises").catch(() => []);
		const normalized = Array.isArray(exercisesData) ? exercisesData : [];
		setPreviewExercises(normalized);
		return normalized;
	};

	const openProgramPreview = async (item) => {
		const athleteIdRaw = String(item?.athlete_id || "").trim();
		if (
			!athleteIdRaw ||
			athleteIdRaw === "null" ||
			athleteIdRaw === "undefined"
		) {
			toast.error(t("feedbackLoadError"));
			return;
		}

		setOpeningProgramPreviewId(item.id);
		try {
			const [athleteActivePrograms, exercisesData] = await Promise.all([
				getAthleteActivePrograms(athleteIdRaw),
				getPreviewExercises(),
			]);

			if (athleteActivePrograms.length === 0) {
				toast.error(t("noActiveProgram"));
				return;
			}

			const sortedActivePrograms = [...athleteActivePrograms].sort((a, b) => {
				const dateA = new Date(a?.created_at || 0).getTime();
				const dateB = new Date(b?.created_at || 0).getTime();
				return dateB - dateA;
			});

			const selectedProgram = sortedActivePrograms[0] || null;
			setSelectedPreviewAthlete({
				user_id: athleteIdRaw,
				name: item?.athlete_name || "-",
			});
			setProgramPreviewState({
				program: selectedProgram,
				programs: sortedActivePrograms,
				exercises: exercisesData,
				selectedProgramId: selectedProgram?.program_id || null,
				weekIndex: 0,
				programCount: sortedActivePrograms.length,
				// Senza questi l'anteprima legge e scrive sull'account del coach:
				// storico vuoto e risultati salvati sulla persona sbagliata.
				athleteId: athleteIdRaw,
				athleteName: item?.athlete_name || null,
			});
			setIsProgramPreviewOpen(true);
		} finally {
			setOpeningProgramPreviewId(null);
		}
	};

	// Programs dropdown: narrow to the selected coach when admin browses "all".
	const coachIdSet = (obj) =>
		new Set([
			...(obj?.coach_ids || []),
			...(obj?.coach_id ? [obj.coach_id] : []),
		]);
	const scopedPrograms =
		isAdmin && scope === "all" && coachFilter !== "all"
			? programs.filter((p) => coachIdSet(p).has(coachFilter))
			: programs;
	// Most-recently created first.
	const sortedPrograms = [...scopedPrograms].sort((a, b) =>
		String(b.created_at || "").localeCompare(String(a.created_at || "")),
	);

	const athleteOptions = [
		{ value: "all", label: t("feedbackAllAthletes") },
		...athletes.map((a) => ({
			value: a.user_id,
			label: a.name,
			sublabel: a.email,
		})),
	];
	const feedProgramOptions = [
		{ value: "all", label: t("feedbackAllPrograms") },
		...sortedPrograms.map((p) => ({
			value: p.program_id,
			label: p.name || p.title,
		})),
	];
	const gridProgramOptions = sortedPrograms.map((p) => ({
		value: p.program_id,
		label: p.name || p.title,
	}));
	const groupOptions = (groups || []).map((g) => ({
		value: g.group_id,
		label: g.name || g.group_id,
		sublabel: t("feedbackGroupMembers").replace(
			"{count}",
			String((g.athlete_ids || []).length),
		),
	}));
	const sortedGroupPrograms = [...(groupTree?.programs || [])].sort((a, b) =>
		(groupProgramRange(b)?.[0] || "").localeCompare(
			groupProgramRange(a)?.[0] || "",
		),
	);
	const groupProgramOptions = sortedGroupPrograms.map((p) => ({
		value: p.program_id,
		label: p.title || p.program_id,
		sublabel: formatGroupRange(groupProgramRange(p), language),
	}));
	const selectedGroupProgram =
		(groupTree?.programs || []).find((p) => p.program_id === groupProgramId) ||
		null;
	const athleteListFiltered = athletes.filter((a) => {
		const q = athleteSearch.trim().toLowerCase();
		if (!q) return true;
		return (
			(a.name || "").toLowerCase().includes(q) ||
			(a.email || "").toLowerCase().includes(q)
		);
	});
	const athleteEmailById = useMemo(() => {
		const map = new Map();
		(athletes || []).forEach((athlete) => {
			if (!athlete?.user_id) return;
			map.set(athlete.user_id, athlete.email || "");
		});
		return map;
	}, [athletes]);

	const todayStr = new Date().toISOString().slice(0, 10);
	const yesterdayStr = (() => {
		const d = new Date();
		d.setDate(d.getDate() - 1);
		return d.toISOString().slice(0, 10);
	})();

	const dateHeading = (key) => {
		if (key === todayStr) return t("today");
		if (key === yesterdayStr) return t("yesterday");
		try {
			return new Date(key).toLocaleDateString(
				language === "it" ? "it-IT" : "en-US",
				{
					weekday: "short",
					day: "numeric",
					month: "short",
				},
			);
		} catch (e) {
			return key;
		}
	};

	const timeAgo = (iso) => {
		if (!iso) return "";
		const then = new Date(iso).getTime();
		if (Number.isNaN(then)) return "";
		const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
		if (mins < 60) return language === "it" ? `${mins}m fa` : `${mins}m ago`;
		const hrs = Math.round(mins / 60);
		if (hrs < 24) return language === "it" ? `${hrs}h fa` : `${hrs}h ago`;
		const days = Math.round(hrs / 24);
		return language === "it" ? `${days}g fa` : `${days}d ago`;
	};

	// Compact, diary-style readout of the concrete result values.
	const resultSummary = (it) => {
		if (it.kind !== "result") return "";
		const parseIntegerTotalReps = (value) => {
			if (value === null || value === undefined) return null;
			const raw = String(value).trim();
			if (!raw) return null;
			const compact = raw.replace(/,/g, ".");
			const fullNumber = compact.match(/^\d+(?:\.0+)?$/);
			const embeddedInteger = compact.match(/\b(\d+)\b/);
			const parsed = Number(fullNumber ? compact : embeddedInteger?.[1]);
			return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
		};
		const resultPayload =
			it?.result && typeof it.result === "object" ? it.result : {};
		const prescriptionPayload =
			it?.prescription && typeof it.prescription === "object"
				? it.prescription
				: {};
		// Tiro: il risultato vive in `shooting` (hit factor, punti, tempi), non
		// nei campi kg/reps/score — senza questo ramo il coach vedeva solo un
		// numero grezzo senza capire se fosse un HF, punti o un tempo.
		const shooting =
			resultPayload?.shooting && typeof resultPayload.shooting === "object"
				? resultPayload.shooting
				: null;
		const exerciseType = String(
			resultPayload?.exercise_type || prescriptionPayload?.exercise_type || "",
		)
			.trim()
			.toLowerCase();
		if (
			shooting ||
			exerciseType === "dry_fire" ||
			exerciseType === "live_fire"
		) {
			const s = shooting || {};
			const mode =
				s.mode || (exerciseType === "dry_fire" ? "dry_fire" : "live_fire");
			const fmtTime = (v) => {
				const n = Number(v);
				return Number.isFinite(n) && n >= 0 ? `${n.toFixed(2)}s` : null;
			};
			const fmtNum = (v) => {
				const n = Number(v);
				if (!Number.isFinite(n)) return null;
				return Number.isInteger(n) ? String(n) : n.toFixed(2);
			};
			if (s.prescription_format === "free") {
				return s.self_rating ? `Autovalutazione ${s.self_rating}/5` : "";
			}
			if (mode === "dry_fire") {
				const parts = [];
				const best = fmtTime(s.best_time_seconds);
				const avg = fmtTime(s.avg_time_seconds);
				if (best) parts.push(best);
				if (avg) parts.push(`media ${avg}`);
				return parts.join(" · ");
			}
			const parts = [];
			const hf = fmtNum(s.hit_factor);
			const pts = fmtNum(s.total_points);
			const tot = fmtTime(s.total_time_seconds);
			if (hf) parts.push(`HF ${hf}`);
			if (pts) parts.push(`${pts} punti`);
			if (tot) parts.push(tot);
			return parts.join(" · ");
		}
		const cfg =
			resultPayload?.conditioning_config &&
			typeof resultPayload.conditioning_config === "object"
				? resultPayload.conditioning_config
				: {};
		const condType = String(
			cfg?.conditioning_type ||
				resultPayload?.conditioning_type ||
				prescriptionPayload?.conditioning_type ||
				"",
		)
			.trim()
			.toLowerCase();
		const scoreType = String(
			cfg?.score_type ||
				resultPayload?.score_type ||
				prescriptionPayload?.score_type ||
				"",
		)
			.trim()
			.toLowerCase();
		const amrapLikeFromCustomTotalReps =
			scoreType === "time_or_reps" &&
			(condType === "amrap" || condType === "free" || condType === "custom");
		const isAmrapLikeResult =
			condType === "amrap" || amrapLikeFromCustomTotalReps;
		if (isAmrapLikeResult) {
			const conditioningPresentation = resolveConditioningResultPresentation({
				result: resultPayload,
				prescriptionExercise: prescriptionPayload,
				programExercise: null,
				language,
			});
			if (conditioningPresentation?.amrapScoreLabel) {
				return conditioningPresentation.amrapScoreLabel;
			}
			const fallbackTotalReps =
				parseIntegerTotalReps(resultPayload?.score) ??
				parseIntegerTotalReps(it?.score) ??
				parseIntegerTotalReps(resultPayload?.reps) ??
				parseIntegerTotalReps(it?.reps) ??
				parseIntegerTotalReps(resultPayload?.reps_text) ??
				parseIntegerTotalReps(it?.reps_text);
			const fallbackLabel = formatAmrapScoreWithRoundsLabel(
				fallbackTotalReps,
				conditioningPresentation?.condExercises ||
					prescriptionPayload?.conditioning_exercises ||
					[],
				language,
			);
			if (fallbackLabel) return fallbackLabel;
		}
		const detailedRows = Array.isArray(it.result_set_rows)
			? it.result_set_rows.filter(Boolean)
			: [];
		if (detailedRows.length > 0) {
			const rowParts = detailedRows
				.map((row, idx) => {
					const prescriptionRowIndex =
						Number.isFinite(Number(row?.row_index)) &&
						Number(row.row_index) >= 0
							? Number(row.row_index)
							: idx;
					const prescribedReps = getStrengthPrescriptionRepsForSet(
						prescriptionPayload,
						prescriptionRowIndex,
					);
					const displayIndex =
						Number.isFinite(Number(row?.row_index)) &&
						Number(row.row_index) >= 0
							? Number(row.row_index) + 1
							: idx + 1;
					const parts = [];
					if (row?.weight_kg) parts.push(`${row.weight_kg} kg`);
					if (row?.reps_text)
						parts.push(formatStrengthRepResult(row.reps_text, prescribedReps));
					else if (row?.reps)
						parts.push(formatStrengthRepResult(row.reps, prescribedReps));
					if (parts.length === 0) return "";
					return `Set ${displayIndex} ${parts.join(" · ")}`;
				})
				.filter(Boolean);
			if (rowParts.length > 0) return rowParts.join(" • ");
		}
		const supersetDetails = Array.isArray(it.superset_details)
			? it.superset_details.filter(Boolean)
			: [];
		if (supersetDetails.length > 0) {
			const detailParts = supersetDetails
				.map((detail, idx) => {
					const parts = [];
					if (detail?.exercise_name) parts.push(detail.exercise_name);
					if (detail?.weight_kg) parts.push(`${detail.weight_kg} kg`);
					if (detail?.reps) parts.push(`${detail.reps} reps`);
					if (parts.length === 0) return "";
					return `${String.fromCharCode(65 + idx)} ${parts.join(" · ")}`;
				})
				.filter(Boolean);
			if (detailParts.length > 0) return detailParts.join(" • ");
		}
		const parts = [];
		if (Array.isArray(it.interval_scores) && it.interval_scores.length > 0) {
			parts.push(it.interval_scores.filter(Boolean).join(" · "));
		} else if (it.sets && it.reps) parts.push(`${it.sets}×${it.reps}`);
		else if (it.reps) parts.push(`${it.reps} reps`);
		if (it.weight_kg) parts.push(`${it.weight_kg} kg`);
		if (it.time_seconds) {
			const m = Math.floor(it.time_seconds / 60);
			const s = it.time_seconds % 60;
			parts.push(`${m}:${String(s).padStart(2, "0")}`);
		}
		if (it.score !== null && it.score !== undefined && it.score !== "")
			parts.push(String(it.score));
		if (it.reps_text) parts.push(it.reps_text);
		return parts.join(" · ");
	};

	const FilterChip = ({ active, onClick, children }) => (
		<button
			onClick={onClick}
			className={`shrink-0 whitespace-nowrap px-3 py-2 sm:px-2.5 sm:py-1.5 rounded-sm text-xs font-medium border transition-colors ${
				active
					? "bg-volt/15 border-volt/50 text-volt"
					: "border-border text-muted-foreground hover:text-foreground hover:border-white/20"
			}`}
		>
			{children}
		</button>
	);

	const ViewTab = ({ id, icon: Icon, label }) => (
		<button
			onClick={() => setView(id)}
			className={`flex items-center justify-center min-w-0 gap-1.5 px-2 sm:px-3 py-2 sm:py-1.5 rounded-sm text-xs sm:text-sm font-medium transition-colors ${
				view === id
					? "bg-volt/15 text-volt"
					: "text-muted-foreground hover:text-foreground hover:bg-white/5"
			}`}
		>
			<Icon className="w-4 h-4" />
			{label}
		</button>
	);

	const renderCard = (it) =>
		(() => {
			const resultPayload =
				it?.result && typeof it.result === "object" ? it.result : {};
			const prescriptionPayload =
				it?.prescription && typeof it.prescription === "object"
					? it.prescription
					: {};
			const resolvedExerciseType = String(
				resultPayload?.exercise_type ||
					prescriptionPayload?.exercise_type ||
					"",
			)
				.trim()
				.toLowerCase();
			const resolvedConditioningType = String(
				resultPayload?.conditioning_type ||
					prescriptionPayload?.conditioning_type ||
					prescriptionPayload?.conditioning_config?.conditioning_type ||
					"",
			)
				.trim()
				.toLowerCase();
			const isConditioningResult =
				resolvedExerciseType === "conditioning" ||
				[
					"amrap",
					"for_time",
					"time_or_reps",
					"emom",
					"hiit",
					"free",
					"custom",
				].includes(resolvedConditioningType) ||
				Array.isArray(prescriptionPayload?.conditioning_exercises) ||
				Boolean(prescriptionPayload?.conditioning_config);
			const isCustomConditioning =
				resolvedConditioningType === "free" ||
				resolvedConditioningType === "custom";
			const conditioningFallbackLabel = isCustomConditioning
				? language === "it"
					? "Conditioning custom"
					: "Custom conditioning"
				: "Conditioning";
			const exerciseDisplayLabel =
				String(it?.exercise_name || "").trim() ||
				(isConditioningResult ? conditioningFallbackLabel : "—");

			const athleteEmail =
				it.athlete_email || athleteEmailById.get(it.athlete_id) || "";
			const hasFutureDate =
				Boolean(it?.date) && String(it.date).slice(0, 10) > todayStr;
			const absoluteTime = it?.created_at
				? new Date(it.created_at).toLocaleString(
						language === "it" ? "it-IT" : "en-US",
						{
							day: "2-digit",
							month: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
						},
					)
				: "";

			return (
				<div
					key={`${it.kind}-${it.id}`}
					className={`rounded-sm border bg-card p-4 transition-colors ${
						it.read
							? "border-border"
							: "border-l-2 border-l-volt border-y-border border-r-border"
					}`}
				>
					<div className="min-w-0 flex-1">
						<div className="flex items-start justify-between gap-2">
							<div className="min-w-0 flex items-center gap-2">
								{!it.read && (
									<span className="h-1.5 w-1.5 rounded-full bg-volt shrink-0" />
								)}
								<div className="min-w-0">
									<span className="font-semibold truncate block">
										{it.athlete_name}
									</span>
									{athleteEmail && (
										<span className="text-[11px] text-muted-foreground/90 truncate block">
											{athleteEmail}
										</span>
									)}
								</div>
							</div>
							<div className="shrink-0 text-right">
								<span
									className={`text-xs font-medium ${
										hasFutureDate ? "text-yellow-300" : "text-foreground/90"
									}`}
								>
									{timeAgo(it.created_at)}
								</span>
								{absoluteTime && (
									<div className="text-[11px] text-muted-foreground mt-0.5">
										{absoluteTime}
									</div>
								)}
							</div>
						</div>

						<div className="mt-1 flex flex-wrap items-center gap-2">
							{it.kind === "diary" ? (
								<span className="inline-flex items-center gap-1 text-sm text-muted-foreground min-w-0 break-words">
									<BookOpen className="w-3.5 h-3.5 shrink-0" />
									<span>
										{t("feedbackDiary")}
										{it.workout_type ? ` · ${it.workout_type}` : ""}
									</span>
								</span>
							) : (
								<span className="inline-flex items-center gap-1 text-sm min-w-0 break-words">
									<Dumbbell className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
									<span>{exerciseDisplayLabel}</span>
								</span>
							)}
							{it.rpe != null && (
								<Badge
									variant="outline"
									className={`text-xs ${
										it.attention
											? "border-blaze/60 text-blaze"
											: "border-border text-muted-foreground"
									}`}
								>
									RPE {it.rpe}
									{it.attention && <AlertTriangle className="w-3 h-3 ml-1" />}
								</Badge>
							)}
						</div>

						{it.kind === "diary" && it.title && (
							<p className="mt-1.5 text-sm font-medium">{it.title}</p>
						)}
						{(() => {
							const summary = resultSummary(it);
							return (
								<>
									{summary && (
										<p className="mt-1.5 text-sm font-mono text-white/80">
											{summary}
										</p>
									)}
									{it.notes ? (
										<p className="mt-1.5 text-sm text-white/90 whitespace-pre-wrap">
											{it.notes}
										</p>
									) : (
										it.kind === "result" &&
										!summary && (
											<p className="mt-1.5 text-sm text-muted-foreground italic">
												{t("feedbackCompleted")}
											</p>
										)
									)}
								</>
							);
						})()}

						{it.kind === "result" && (it.program_name || it.day_label) && (
							<p className="mt-1.5 text-xs text-muted-foreground">
								{it.program_name}
								{it.program_type === "group" && (
									<span className="ml-1 uppercase">· {t("feedbackGroup")}</span>
								)}
								{it.day_label ? ` · ${it.day_label}` : ""}
								{hasFutureDate && (
									<span className="ml-1 text-yellow-300 uppercase">
										· Futuro
									</span>
								)}
							</p>
						)}

						{/* Actions — mobile: primary action + overflow menu, detail toggle on its own row */}
						<div className="mt-3 space-y-2 sm:hidden">
							<div className="flex items-center gap-2">
								{getFeedbackThreadId(it) ? (
									<Button
										variant="outline"
										className="h-10 flex-1 min-w-0 justify-start px-3 rounded-sm border-volt/40 bg-volt/10 text-volt hover:bg-volt/15 hover:text-volt text-sm"
										onClick={() => goToFeedbackThread(it)}
									>
										<ChevronRight className="w-4 h-4 shrink-0" />
										<span className="truncate">
											{language === "it" ? "Conversazione" : "Conversation"}
										</span>
									</Button>
								) : (
									<Button
										variant="outline"
										className="h-10 flex-1 min-w-0 justify-start px-3 rounded-sm border-border text-sm"
										onClick={() =>
											replyingId === it.id
												? setReplyingId(null)
												: startReply(it)
										}
									>
										<Reply className="w-4 h-4 shrink-0" />
										<span className="truncate">{t("feedbackReply")}</span>
									</Button>
								)}
								{!it.read && (
									<Button
										variant="outline"
										className="h-10 w-10 shrink-0 p-0 rounded-sm border-volt/40 text-volt hover:text-volt"
										onClick={() => markRead([it], true)}
										aria-label={t("feedbackMarkReadOne")}
										title={t("feedbackMarkReadOne")}
									>
										<Check className="w-4 h-4" />
									</Button>
								)}
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="outline"
											className="h-10 w-10 shrink-0 p-0 rounded-sm border-border text-muted-foreground"
											aria-label={
												language === "it" ? "Altre azioni" : "More actions"
											}
										>
											<MoreHorizontal className="w-4 h-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align="end"
										className="w-56 bg-card border-border"
									>
										<DropdownMenuItem
											className="text-sm"
											onClick={() => openAthlete(it.athlete_id)}
										>
											<User className="w-4 h-4 mr-2" />
											{t("feedbackOpenAthlete")}
										</DropdownMenuItem>
										{it.kind === "result" && (
											<DropdownMenuItem
												className="text-sm"
												disabled={openingProgramPreviewId === it.id}
												onClick={() => openProgramPreview(it)}
											>
												{openingProgramPreviewId === it.id ? (
													<Loader2 className="w-4 h-4 mr-2 animate-spin" />
												) : (
													<Eye className="w-4 h-4 mr-2" />
												)}
												{t("feedbackOpenProgram")}
											</DropdownMenuItem>
										)}
										{it.kind === "result" && (
											<DropdownMenuItem
												className="text-sm"
												onClick={() => openExerciseHistory(it)}
											>
												<History className="w-4 h-4 mr-2" />
												{t("exerciseHistoryLabel") || "Storico"}
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
							{it.kind === "result" && (
								<Button
									variant="ghost"
									className="h-10 w-full justify-between px-3 rounded-sm border border-border/60 text-sm text-muted-foreground hover:text-foreground"
									onClick={() =>
										setExpandedKey((k) =>
											k === `${it.kind}-${it.id}`
												? null
												: `${it.kind}-${it.id}`,
										)
									}
								>
									<span className="truncate">{t("feedbackDetail")}</span>
									<ChevronDown
										className={`w-4 h-4 shrink-0 transition-transform ${
											expandedKey === `${it.kind}-${it.id}` ? "rotate-180" : ""
										}`}
									/>
								</Button>
							)}
						</div>

						{/* Actions — desktop/tablet */}
						<div className="mt-3 hidden sm:flex flex-wrap items-center gap-2">
							{getFeedbackThreadId(it) ? (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-volt hover:text-volt"
									onClick={() => goToFeedbackThread(it)}
								>
									<ChevronRight className="w-4 h-4 mr-1.5" />
									{t("msgGoToThread")}
								</Button>
							) : (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-muted-foreground hover:text-foreground"
									onClick={() =>
										replyingId === it.id ? setReplyingId(null) : startReply(it)
									}
								>
									<Reply className="w-4 h-4 mr-1.5" />
									{t("feedbackReply")}
								</Button>
							)}
							<Button
								variant="ghost"
								size="sm"
								className="h-8 px-2 text-sm text-muted-foreground hover:text-volt"
								onClick={() => openAthlete(it.athlete_id)}
							>
								{t("feedbackOpenAthlete")}
								<ChevronRight className="w-4 h-4 ml-0.5" />
							</Button>
							{it.kind === "result" && (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-muted-foreground hover:text-volt"
									onClick={() => openProgramPreview(it)}
									disabled={openingProgramPreviewId === it.id}
								>
									{openingProgramPreviewId === it.id ? (
										<Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
									) : (
										<Eye className="w-4 h-4 mr-1.5" />
									)}
									{t("feedbackOpenProgram")}
								</Button>
							)}
							{it.kind === "result" && (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-muted-foreground hover:text-foreground"
									onClick={() =>
										setExpandedKey((k) =>
											k === `${it.kind}-${it.id}`
												? null
												: `${it.kind}-${it.id}`,
										)
									}
								>
									<ChevronDown
										className={`w-4 h-4 mr-1.5 transition-transform ${
											expandedKey === `${it.kind}-${it.id}` ? "rotate-180" : ""
										}`}
									/>
									{t("feedbackDetail")}
								</Button>
							)}
							{it.kind === "result" && (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-muted-foreground hover:text-volt"
									onClick={() => openExerciseHistory(it)}
								>
									<History className="w-4 h-4 mr-1.5" />
									{t("exerciseHistoryLabel") || "Storico"}
								</Button>
							)}
							{!it.read && (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 px-2 text-sm text-muted-foreground hover:text-volt ml-auto"
									onClick={() => markRead([it], true)}
								>
									<Check className="w-4 h-4 mr-1.5" />
									{t("feedbackMarkReadOne")}
								</Button>
							)}
						</div>

						{replyingId === it.id && !getFeedbackThreadId(it) && (
							<div className="mt-2 space-y-2">
								<Textarea
									value={replyText}
									onChange={(e) => setReplyText(e.target.value)}
									placeholder={t("feedbackReplyPlaceholder")}
									className="bg-secondary border-border rounded-sm min-h-[70px] text-sm"
									autoFocus
								/>
								<div className="flex justify-end gap-2">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setReplyingId(null)}
									>
										{t("cancel")}
									</Button>
									<Button
										size="sm"
										className="bg-volt text-black font-bold rounded-sm hover:bg-volt/90"
										disabled={sending || !replyText.trim()}
										onClick={() => sendReply(it)}
									>
										{sending ? (
											<Loader2 className="w-4 h-4 animate-spin" />
										) : (
											<>
												<Send className="w-4 h-4 mr-1.5" />
												{t("feedbackSend")}
											</>
										)}
									</Button>
								</div>
							</div>
						)}

						{it.kind === "result" && expandedKey === `${it.kind}-${it.id}` && (
							<div className="mt-3 border border-border/50 rounded-sm bg-secondary/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
								<DetailErrorBoundary fallback={t("feedbackCompleted")}>
									<ExerciseDetailAccordion
										exercise={
											it.prescription
												? { ...it.prescription, result: it.result }
												: {
														...(it.result || {}),
														exercise_name: it.exercise_name,
														result: it.result,
													}
										}
										language={language}
										isCoachView={true}
									/>
								</DetailErrorBoundary>
							</div>
						)}
					</div>
				</div>
			);
		})();

	const renderFeedList = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center py-20 text-muted-foreground">
					<Loader2 className="w-5 h-5 animate-spin mr-2" />
					{t("loading")}
				</div>
			);
		}
		if (items.length === 0) {
			return (
				<div className="text-center py-20 text-muted-foreground">
					<Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
					<p>{t("feedbackEmpty")}</p>
				</div>
			);
		}
		// Group by workout date.
		const map = new Map();
		for (const it of items) {
			const key = (it.date || it.created_at || "").slice(0, 10) || "—";
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(it);
		}
		const groups = Array.from(map.entries()).sort((a, b) => {
			const aKey = a[0];
			const bKey = b[0];
			const aFuture = aKey > todayStr;
			const bFuture = bKey > todayStr;
			if (aFuture !== bFuture) return aFuture ? 1 : -1;
			return String(bKey).localeCompare(String(aKey));
		});
		return (
			<div className="space-y-6">
				{groups.map(([dateKey, groupItems]) => (
					<div key={dateKey} className="space-y-2">
						<div
							className={`text-xs font-mono uppercase tracking-widest py-1 ${
								dateKey > todayStr ? "text-yellow-300/80" : "text-white/70"
							}`}
						>
							{dateHeading(dateKey)}
						</div>
						{groupItems.map(renderCard)}
					</div>
				))}
				{nextBefore && (
					<div className="flex justify-center pt-2">
						<Button
							variant="outline"
							onClick={loadMore}
							disabled={loadingMore}
							className="rounded-sm border-border"
						>
							{loadingMore ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							{t("feedbackLoadMore")}
						</Button>
					</div>
				)}
			</div>
		);
	};

	// --- Program grid view ---
	const renderProgramGrid = () => {
		const week = grid?.weeks?.[gridWeekIdx];
		return (
			<div className="space-y-4">
				<div className="flex flex-col sm:flex-row sm:items-center gap-2">
					<SearchableSelect
						value={gridProgramId}
						onChange={(v) => {
							setGridProgramId(v);
							loadGrid(v);
						}}
						options={gridProgramOptions}
						placeholder={t("feedbackSelectProgram")}
						searchPlaceholder={t("feedbackSearchProgram")}
						emptyText={t("feedbackEmpty")}
						triggerClassName="w-full sm:w-[240px]"
					/>

					{grid?.weeks?.length > 1 && (
						<div className="flex items-center justify-between gap-1 w-full sm:w-auto sm:ml-auto">
							<Button
								variant="outline"
								size="icon"
								className="h-9 w-9 rounded-sm border-border"
								disabled={gridWeekIdx === 0}
								onClick={() => setGridWeekIdx((i) => Math.max(0, i - 1))}
							>
								<ChevronLeft className="w-4 h-4" />
							</Button>
							<span className="text-sm font-medium px-2 min-w-[110px] text-center flex-1 sm:flex-none">
								{week?.label}
							</span>
							<Button
								variant="outline"
								size="icon"
								className="h-9 w-9 rounded-sm border-border"
								disabled={gridWeekIdx >= grid.weeks.length - 1}
								onClick={() =>
									setGridWeekIdx((i) => Math.min(grid.weeks.length - 1, i + 1))
								}
							>
								<ChevronRight className="w-4 h-4" />
							</Button>
						</div>
					)}
				</div>

				{gridLoading ? (
					<div className="flex items-center justify-center py-20 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin mr-2" />
						{t("loading")}
					</div>
				) : !grid || !grid.athletes?.length ? (
					<div className="text-center py-20 text-muted-foreground">
						<p>{t("feedbackNoAthletes")}</p>
					</div>
				) : !week?.days?.length ? (
					<div className="text-center py-20 text-muted-foreground">
						<p>{t("feedbackEmpty")}</p>
					</div>
				) : (
					<div className="overflow-x-auto border border-border rounded-sm">
						<table className="w-full text-sm border-collapse">
							<thead>
								<tr className="bg-secondary/60">
									<th className="text-left font-medium p-2 sticky left-0 bg-secondary/60 min-w-[140px]">
										{t("feedbackColAthlete")}
									</th>
									{week.days.map((d) => (
										<th
											key={d.day_id}
											className="font-medium p-2 text-center whitespace-nowrap min-w-[88px]"
										>
											{d.label || "—"}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{grid.athletes.map((a) => (
									<tr key={a.athlete_id} className="border-t border-border">
										<td className="p-2 sticky left-0 bg-card font-medium truncate max-w-[180px]">
											{a.name}
										</td>
										{week.days.map((d) => {
											const cell = grid.cells?.[`${a.athlete_id}|${d.day_id}`];
											return (
												<td key={d.day_id} className="p-1.5 text-center">
													<button
														onClick={() => {
															if (!cell) return;
															// Drill into the feed: this athlete's full activity in
															// the program (completed results too, no date cap).
															setAthleteId(a.athlete_id);
															setProgramId(grid.program.program_id);
															setIncludeCompleted(true);
															setPeriod("all");
															setView("feed");
														}}
														disabled={!cell}
														className={`w-full rounded-sm py-1.5 px-1 text-xs font-medium transition-colors ${
															!cell
																? "text-muted-foreground/40"
																: cell.attention
																	? "bg-blaze/15 text-blaze hover:bg-blaze/25"
																	: "bg-volt/10 text-volt hover:bg-volt/20"
														}`}
														title={cell ? t("feedbackOpenAthlete") : ""}
													>
														{!cell ? (
															"—"
														) : cell.max_rpe != null ? (
															<span className="inline-flex items-center gap-0.5">
																<Check className="w-3 h-3" />
																RPE {cell.max_rpe}
															</span>
														) : cell.has_note ? (
															"💬"
														) : (
															<Check className="w-3.5 h-3.5 inline" />
														)}
													</button>
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<p className="text-xs text-muted-foreground">{t("feedbackGridHint")}</p>
			</div>
		);
	};

	// --- Group leaderboards view (agenda → per-exercise group leaderboard) ---
	const renderGroupView = () => {
		const week = selectedGroupProgram?.weeks?.[groupWeekIdx];
		const weekDates =
			week && groupWeekRange(selectedGroupProgram, week, groupWeekIdx);
		return (
			<div className="space-y-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
					<SearchableSelect
						value={groupId}
						onChange={(value) => {
							setGroupProgramId("");
							setGroupId(value);
						}}
						options={groupOptions}
						placeholder={t("feedbackSelectGroup")}
						searchPlaceholder={t("feedbackSearchGroup")}
						emptyText={t("feedbackNoGroups")}
						triggerClassName="w-full min-w-0 h-auto min-h-11"
						showDetails
					/>
					{groupProgramOptions.length > 0 && (
						<SearchableSelect
							value={groupProgramId}
							onChange={(v) => {
								setGroupProgramId(v);
							}}
							options={groupProgramOptions}
							placeholder={t("feedbackSelectProgram")}
							searchPlaceholder={t("feedbackSearchProgram")}
							emptyText={t("feedbackEmpty")}
							triggerClassName="w-full min-w-0 h-auto min-h-11"
							showDetails
						/>
					)}
				</div>
				{selectedGroupProgram?.weeks?.length > 0 && (
					<div className="flex items-center justify-between gap-1 min-w-0">
						<Button
							variant="outline"
							size="icon"
							className="h-9 w-9 rounded-sm border-border"
							disabled={groupWeekIdx === 0}
							onClick={() => setGroupWeekIdx((i) => Math.max(0, i - 1))}
						>
							<ChevronLeft className="w-4 h-4" />
						</Button>
						<span className="text-sm font-medium px-2 text-center flex-1 min-w-0">
							{week?.label}
							{weekDates && (
								<span className="block text-xs font-normal text-muted-foreground">
									{formatGroupRange(weekDates, language)}
								</span>
							)}
						</span>
						<Button
							variant="outline"
							size="icon"
							className="h-9 w-9 rounded-sm border-border"
							disabled={groupWeekIdx >= selectedGroupProgram.weeks.length - 1}
							onClick={() =>
								setGroupWeekIdx((i) =>
									Math.min(selectedGroupProgram.weeks.length - 1, i + 1),
								)
							}
						>
							<ChevronRight className="w-4 h-4" />
						</Button>
					</div>
				)}

				{groupTreeLoading ? (
					<div className="flex items-center justify-center py-20 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin mr-2" />
						{t("loading")}
					</div>
				) : !groupId ? (
					<div className="text-center py-20 text-muted-foreground">
						<p>{t("feedbackNoGroups")}</p>
					</div>
				) : !selectedGroupProgram ? (
					<div className="text-center py-20 text-muted-foreground">
						<p>{t("feedbackGroupNoPrograms")}</p>
					</div>
				) : !week?.days?.length ? (
					<div className="text-center py-20 text-muted-foreground">
						<p>{t("feedbackEmpty")}</p>
					</div>
				) : (
					<div className="space-y-3">
						{week.days.map((day) => (
							<div
								key={day.day_id}
								className="border border-border rounded-sm overflow-hidden"
							>
								<div className="bg-secondary/60 px-3 py-2 font-heading text-sm">
									{day.label || "—"}
									{day.date && (
										<span className="ml-2 text-xs font-normal text-muted-foreground">
											{formatGroupDay(day.date, language)}
										</span>
									)}
								</div>
								{day.exercises.length === 0 ? (
									<p className="px-3 py-3 text-xs text-muted-foreground">
										{t("feedbackEmpty")}
									</p>
								) : (
									<ul className="divide-y divide-border">
										{day.exercises.map((ex, idx) => {
											const hasResults = (ex.results_count || 0) > 0;
											const rowKey =
												ex.exercise_id ||
												String(ex.exercise_name || "")
													.trim()
													.toLowerCase() ||
												`idx-${idx}`;
											const dayKey = `${selectedGroupProgram.program_id}|${day.day_id}`;
											const expanded = Boolean(
												expandedGroupRows[`${dayKey}|${rowKey}`],
											);
											const detail = groupDayDetails[dayKey];
											return (
												<li
													key={ex.exercise_id || `${ex.exercise_name}-${idx}`}
													className="px-3 py-2"
												>
													<div className="flex items-center gap-2">
														<button
															type="button"
															onClick={() =>
																toggleGroupExerciseDetail(
																	selectedGroupProgram.program_id,
																	day.day_id,
																	rowKey,
																)
															}
															aria-expanded={expanded}
															title={t("feedbackGroupShowPrescription")}
															className="min-w-0 flex-1 flex items-start gap-2 text-left"
														>
															{expanded ? (
																<ChevronDown className="w-3.5 h-3.5 mt-1 shrink-0 text-muted-foreground" />
															) : (
																<ChevronRight className="w-3.5 h-3.5 mt-1 shrink-0 text-muted-foreground" />
															)}
															<span className="min-w-0 flex-1">
																<span className="block text-sm font-medium break-words">
																	{ex.exercise_name || "-"}
																</span>
																<span className="block text-[11px] text-muted-foreground">
																	{hasResults
																		? t("feedbackGroupExerciseResults").replace(
																				"{count}",
																				String(ex.results_count),
																			)
																		: t("feedbackGroupNoResultsYet")}
																</span>
															</span>
														</button>
														<Button
															variant="outline"
															size="sm"
															disabled={!hasResults}
															onClick={() =>
																openGroupLeaderboard(
																	selectedGroupProgram,
																	day,
																	ex,
																)
															}
															className="h-8 rounded-sm border-border shrink-0"
															title={t("feedbackGroupOpenLeaderboard")}
														>
															<Trophy className="w-3.5 h-3.5 sm:mr-1.5 text-yellow-500" />
															<span className="hidden sm:inline">
																{t("feedbackGroupOpenLeaderboard")}
															</span>
														</Button>
													</div>
													{expanded && (
														<GroupExercisePrescription
															detail={detail}
															row={ex}
															language={language}
															t={t}
														/>
													)}
												</li>
											);
										})}
									</ul>
								)}
							</div>
						))}
					</div>
				)}
				<p className="text-xs text-muted-foreground">
					{t("feedbackGroupHint")}
				</p>

				<LeaderboardDialog
					open={glbOpen}
					onOpenChange={(open) => {
						setGlbOpen(open);
						if (!open) {
							setGlbData(null);
							setGlbError("");
							setGlbExercise(null);
							setGlbGenderFilter("all");
							setGlbSchemaFieldId(null);
						}
					}}
					title={t("leaderboardTitle")}
					description={glbExercise?.label || ""}
					loading={glbLoading}
					error={glbError}
					data={glbData}
					showPrivacyBadges
					genderFilter={glbGenderFilter}
					onGenderFilterChange={(g) => {
						setGlbGenderFilter(g);
						loadGroupLeaderboard(glbExercise, {
							gender: g,
							schemaFieldId: glbSchemaFieldId,
						});
					}}
					schemaFields={glbData?.result_schema || []}
					activeSchemaFieldId={
						glbData?.active_schema_field_id || glbSchemaFieldId
					}
					onActiveSchemaFieldChange={(fid) => {
						setGlbSchemaFieldId(fid);
						loadGroupLeaderboard(glbExercise, {
							gender: glbGenderFilter,
							schemaFieldId: fid,
						});
					}}
					formatMetric={(entry) =>
						formatLeaderboardMetric(
							entry,
							t,
							glbData,
							language,
							glbData?.conditioning_exercises || [],
							String(glbData?.conditioning_type || "")
								.trim()
								.toLowerCase(),
						)
					}
					getEntryMetaLabel={() => ""}
					t={t}
					emptyDescription={t("feedbackGroupNoResultsYet")}
				/>
			</div>
		);
	};

	// --- Athlete view (list + filtered feed) ---
	const renderAthleteView = () => (
		<div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
			<div className="border border-border rounded-sm">
				<div className="relative p-1.5 border-b border-border">
					<Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={athleteSearch}
						onChange={(e) => setAthleteSearch(e.target.value)}
						placeholder={t("feedbackSearchAthlete")}
						className="pl-8 h-9 bg-card border-transparent rounded-sm text-sm"
					/>
				</div>
				<div className="space-y-1 md:max-h-[65vh] md:overflow-y-auto p-1.5">
					{athleteListFiltered.length === 0 ? (
						<p className="text-sm text-muted-foreground p-3">
							{t("feedbackNoAthletes")}
						</p>
					) : (
						athleteListFiltered.map((a) => (
							<button
								key={a.user_id}
								onClick={() => setAthleteId(a.user_id)}
								className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors flex items-center gap-2 ${
									athleteId === a.user_id
										? "bg-volt/15 text-volt"
										: "text-muted-foreground hover:text-foreground hover:bg-white/5"
								}`}
							>
								<User className="w-4 h-4 shrink-0" />
								<span className="flex flex-col min-w-0">
									<span className="truncate">{a.name}</span>
									{a.email && (
										<span className="truncate text-xs opacity-70">
											{a.email}
										</span>
									)}
								</span>
							</button>
						))
					)}
				</div>
			</div>
			<div>
				{athleteId === "all" ? (
					<div className="text-center py-20 text-muted-foreground">
						<User className="w-10 h-10 mx-auto mb-3 opacity-40" />
						<p>{t("feedbackSelectAthlete")}</p>
					</div>
				) : (
					<>
						<div className="flex flex-wrap items-center gap-2 mb-4">
							<FilterChip
								active={includeCompleted}
								onClick={() => setIncludeCompleted((v) => !v)}
							>
								{t("feedbackIncludeCompleted")}
							</FilterChip>
							<FilterChip
								active={attention}
								onClick={() => setAttention((v) => !v)}
							>
								<span className="inline-flex items-center gap-1">
									<AlertTriangle className="w-3.5 h-3.5" />
									{t("feedbackNeedsAttention")}
								</span>
							</FilterChip>
						</div>
						{renderFeedList()}
					</>
				)}
			</div>
		</div>
	);

	return (
		<Layout>
			<div className="max-w-4xl mx-auto px-4 py-6 pb-24 sm:pb-6 space-y-5">
				{/* Header */}
				<div className="flex items-start justify-between gap-3 flex-wrap">
					<div>
						<h1 className="text-2xl font-heading font-extrabold uppercase tracking-tight flex items-center gap-2">
							<Inbox className="w-6 h-6 text-volt" />
							{t("feedback")}
							{feedbackUnreadCount > 0 && (
								<Badge className="bg-blaze text-white">
									{feedbackUnreadCount}
								</Badge>
							)}
						</h1>
						<p className="hidden sm:block text-sm text-muted-foreground mt-1">
							{t("feedbackSubtitle")}
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={markAllRead}
						className="h-9 w-auto rounded-sm border-border"
					>
						<CheckCheck className="w-4 h-4 mr-1.5" />
						{t("feedbackMarkAllRead")}
					</Button>
				</div>

				{/* View toggle + (admin) visibility scope */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
					<div className="grid grid-cols-2 gap-1 border border-border rounded-sm p-1 w-full sm:w-fit sm:flex sm:items-center">
						<ViewTab id="feed" icon={List} label={t("feedbackViewFeed")} />
						<ViewTab
							id="program"
							icon={LayoutGrid}
							label={t("feedbackViewProgram")}
						/>
						<ViewTab
							id="athlete"
							icon={User}
							label={t("feedbackViewAthlete")}
						/>
						<ViewTab id="group" icon={Users} label={t("feedbackViewGroup")} />
					</div>

					{isAdmin && (
						<div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
							<div className="grid grid-cols-2 gap-1 border border-border rounded-sm p-1 w-full sm:w-auto">
								<button
									onClick={() => setScope("mine")}
									className={`px-3 py-1 rounded-sm text-sm font-medium transition-colors w-full ${
										scope === "mine"
											? "bg-volt/15 text-volt"
											: "text-muted-foreground hover:text-foreground hover:bg-white/5"
									}`}
								>
									{t("feedbackScopeMine")}
								</button>
								<button
									onClick={() => setScope("all")}
									className={`px-3 py-1 rounded-sm text-sm font-medium transition-colors w-full ${
										scope === "all"
											? "bg-volt/15 text-volt"
											: "text-muted-foreground hover:text-foreground hover:bg-white/5"
									}`}
								>
									{t("feedbackScopeAll")}
								</button>
							</div>
							{scope === "all" && (
								<Select value={coachFilter} onValueChange={setCoachFilter}>
									<SelectTrigger className="w-full sm:w-[180px] h-9 bg-card border-border rounded-sm text-sm">
										<SelectValue placeholder={t("feedbackCoachFilter")} />
									</SelectTrigger>
									<SelectContent className="bg-card border-border max-h-72">
										<SelectItem value="all">
											{t("feedbackAllCoaches")}
										</SelectItem>
										{coaches.map((c) => (
											<SelectItem key={c.user_id} value={c.user_id}>
												{c.name || c.email}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						</div>
					)}
				</div>

				{view === "feed" && (
					<>
						<div className="space-y-2">
							<div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto pb-1 sm:hidden">
								<FilterChip
									active={!onlyUnread && !attention}
									onClick={() => {
										setOnlyUnread(false);
										setAttention(false);
									}}
								>
									{t("feedbackAll")}
								</FilterChip>
								<FilterChip
									active={onlyUnread}
									onClick={() => setOnlyUnread((v) => !v)}
								>
									{t("feedbackUnreadOnly")}
								</FilterChip>
								<FilterChip
									active={includeCompleted}
									onClick={() => setIncludeCompleted((v) => !v)}
								>
									{t("feedbackIncludeCompleted")}
								</FilterChip>
								<button
									type="button"
									onClick={() => setMobileFiltersOpen((v) => !v)}
									className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-border text-xs text-muted-foreground hover:text-foreground"
								>
									{language === "it" ? "Filtri" : "Filters"}
									<ChevronDown
										className={`w-3.5 h-3.5 transition-transform ${
											mobileFiltersOpen ? "rotate-180" : ""
										}`}
									/>
								</button>
							</div>

							<div className="hidden sm:flex flex-wrap items-center gap-2">
								<FilterChip
									active={!onlyUnread && !attention}
									onClick={() => {
										setOnlyUnread(false);
										setAttention(false);
									}}
								>
									{t("feedbackAll")}
								</FilterChip>
								<FilterChip
									active={onlyUnread}
									onClick={() => setOnlyUnread((v) => !v)}
								>
									{t("feedbackUnreadOnly")}
								</FilterChip>
								<FilterChip
									active={attention}
									onClick={() => setAttention((v) => !v)}
								>
									<span className="inline-flex items-center gap-1">
										<AlertTriangle className="w-3.5 h-3.5" />
										{t("feedbackNeedsAttention")}
									</span>
								</FilterChip>
								<FilterChip
									active={includeCompleted}
									onClick={() => setIncludeCompleted((v) => !v)}
								>
									{t("feedbackIncludeCompleted")}
								</FilterChip>
							</div>

							<div
								className={`grid grid-cols-1 sm:grid-cols-3 gap-2 ${
									mobileFiltersOpen ? "" : "hidden sm:grid"
								}`}
							>
								<SearchableSelect
									value={athleteId}
									onChange={setAthleteId}
									options={athleteOptions}
									searchPlaceholder={t("feedbackSearchAthlete")}
									emptyText={t("feedbackNoAthletes")}
									triggerClassName="w-full"
								/>

								<SearchableSelect
									value={programId}
									onChange={setProgramId}
									options={feedProgramOptions}
									searchPlaceholder={t("feedbackSearchProgram")}
									emptyText={t("feedbackEmpty")}
									triggerClassName="w-full"
								/>

								<Select value={period} onValueChange={setPeriod}>
									<SelectTrigger className="w-full h-9 bg-card border-border rounded-sm text-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="7">{t("feedbackPeriod7")}</SelectItem>
										<SelectItem value="30">{t("feedbackPeriod30")}</SelectItem>
										<SelectItem value="all">
											{t("feedbackPeriodAll")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						{renderFeedList()}
					</>
				)}

				{view === "program" && renderProgramGrid()}
				{view === "athlete" && renderAthleteView()}
				{view === "group" && renderGroupView()}

				<Dialog
					open={isProgramPreviewOpen}
					onOpenChange={(open) => {
						setIsProgramPreviewOpen(open);
						if (!open) {
							setProgramPreviewState(null);
							setSelectedPreviewAthlete(null);
						}
					}}
				>
					<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-hidden">
						<DialogDescription className="hidden" />
						<DialogHeader className="pt-3 md:pt-0">
							<DialogTitle className="font-heading">
								{t("feedbackOpenProgram")} ·{" "}
								{selectedPreviewAthlete?.name || "-"}
							</DialogTitle>
						</DialogHeader>

						<div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4 h-full">
							<div className="space-y-3 min-w-0 lg:max-w-[260px] hidden md:block">
								<div className="rounded-sm border border-border bg-secondary/30 p-3 space-y-2">
									<p className="text-xs uppercase tracking-wider text-muted-foreground">
										Contesto anteprima
									</p>
									<div className="grid gap-2 text-xs">
										<div className="rounded-sm bg-background border border-border px-2 py-1.5">
											<span className="text-muted-foreground">Programma:</span>{" "}
											{programPreviewState?.program?.title || "-"}
										</div>
										<div className="rounded-sm bg-background border border-border px-2 py-1.5">
											<span className="text-muted-foreground">Tipo:</span>{" "}
											{programPreviewState?.program?.type === "group"
												? "Gruppo"
												: "Individuale"}
										</div>
										<div className="rounded-sm bg-background border border-border px-2 py-1.5">
											<span className="text-muted-foreground">
												Programmi assegnati:
											</span>{" "}
											{programPreviewState?.programCount || 0}
										</div>
									</div>
								</div>
							</div>

							<div className="mx-auto w-[390px] max-w-full rounded-[28px] border border-border bg-black p-2 shadow-2xl shadow-black/60">
								<div className="rounded-[22px] border border-border bg-background h-[72vh] overflow-y-auto">
									{programPreviewState?.program ? (
										<AthleteDashboard
											previewState={programPreviewState}
											containerClassName="max-w-none mx-0 px-3 pb-6"
										/>
									) : (
										<div className="h-full flex items-center justify-center text-sm text-muted-foreground">
											{t("noAssignedProgramsPreview")}
										</div>
									)}
								</div>
							</div>
						</div>
					</DialogContent>
				</Dialog>

				<ExerciseHistoryDialog
					open={historyOpen}
					onOpenChange={(open) => {
						setHistoryOpen(open);
						if (!open) {
							setHistoryExercise(null);
							setHistoryResults([]);
							setHistoryError("");
						}
					}}
					exercise={historyExercise}
					loading={historyLoading}
					error={historyError}
					results={historyResults}
					t={t}
				/>
			</div>
		</Layout>
	);
}
